/**
 * Warranty Center status helpers.
 *
 * Chip colours match OverviewTab.endDateColor: red < 30 days, amber < 90, green otherwise.
 * HP vs HPE: HPE is a different company and falls through to the Lansweeper path.
 */

export function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

export function endDateColor(iso) {
  const days = daysUntil(iso);
  if (days === null) return 'default';
  if (days < 30) return 'error';
  if (days < 90) return 'warning';
  return 'success';
}

export function fmtDate(iso) {
  return iso ? String(iso).slice(0, 10) : '—';
}

export function isHpManufacturer(raw) {
  const s = String(raw || '').trim().toUpperCase();
  if (!s) return false;
  if (
    s === 'HPE' ||
    s.startsWith('HPE ') ||
    s.includes('HEWLETT PACKARD ENTERPRISE') ||
    s.includes('HEWLETT-PACKARD ENTERPRISE')
  ) {
    return false;
  }
  return (
    s === 'HP' ||
    s.startsWith('HP ') ||
    s === 'HP INC' ||
    s === 'HP INC.' ||
    s === 'HEWLETT-PACKARD' ||
    s.startsWith('HEWLETT-PACKARD') ||
    s === 'HEWLETT PACKARD' ||
    s.startsWith('HEWLETT PACKARD')
  );
}

export function serialOf(device) {
  const v = device?.udf?.udf31;
  if (v == null) return '';
  return String(v).trim();
}

export function manufacturerOf(device) {
  const v = device?.udf?.udf32;
  if (v == null) return '';
  return String(v).trim();
}

export function modelOf(device) {
  const v = device?.udf?.udf33;
  if (v == null) return '';
  return String(v).trim();
}

export function skuOf(device) {
  const v = device?.udf?.udf37;
  if (v == null) return '';
  return String(v).trim();
}

export function typeOf(device) {
  const dt = device?.deviceType;
  if (!dt) return '';
  if (typeof dt === 'string') return dt;
  return dt.deviceType || dt.category || dt.type || '';
}

export function warrantyDateOf(device) {
  const v = device?.warrantyDate;
  if (v == null || v === '') return '';
  return String(v);
}

/**
 * Status for one Datto device. When a warrantyDate is on file, kind is 'date'
 * (render the same 30/90-day chip). Otherwise a lookup-state string.
 *
 * Evaluation order: date on file → missing serial → HP pending → Lansweeper
 * empty / not supported.
 */
export function warrantyStatus(device, { hasLansweeperId } = {}) {
  const date = warrantyDateOf(device);
  if (date) {
    return { kind: 'date', date, label: fmtDate(date), color: endDateColor(date) };
  }
  if (!serialOf(device)) {
    return { kind: 'text', label: 'Serial unknown — pending audit', color: 'default' };
  }
  if (isHpManufacturer(manufacturerOf(device))) {
    return { kind: 'text', label: 'Pending lookup', color: 'default' };
  }
  if (hasLansweeperId) {
    return { kind: 'text', label: 'No warranty data from Lansweeper', color: 'default' };
  }
  return { kind: 'text', label: 'Warranty lookup not supported', color: 'default' };
}
